/**
 * @description Service layer for the Hotel Admin Edit screen.
 *              Wraps the two ops-facing endpoints documented in the backend
 *              "Hotel Admin Edit — Frontend Integration Guide":
 *                fetchHotelBooking  -> POST /hotel/adminedit/fetch
 *                updateHotelBooking -> POST /hotel/adminedit/update
 *
 *              Runs on the API gateway like every other beadmin domain
 *              (encrypted request + decrypted response, beadmin auth headers) via
 *              apiClient. Gateway routes forward to the booking engine:
 *                POST /app/v1/beadmin/hotel/adminedit/fetch
 *                     (engine POST /be/v1/hotel/adminedit/fetch)
 *                POST /app/v1/beadmin/hotel/adminedit/update
 *                     (engine POST /be/v1/hotel/adminedit/update)
 *
 *              A fresh x-trace-id is generated per call (echoed by the backend,
 *              surfaced in the UI on error). NOTE: like the promo / manual-booking
 *              routes, the gateway mapping for /hotel/adminedit/* may still be
 *              pending — this file is the single place to switch to a direct
 *              engine path if the gateway is not wired.
 */

//********IMPORTS STARTS*********/
import { BE_ADMIN_BASE } from "./environment";
// @ts-ignore -- apiClient is JS; named exports are valid at runtime
import { apiPost, genTraceId } from "./apiClient";
import { getUserDetails } from "./authSession";
//********IMPORTS ENDS*********/

//********CONSTANTS STARTS*********/
const HOTEL_EDIT_BASE = `${BE_ADMIN_BASE}/hotel/adminedit`;
//********CONSTANTS ENDS*********/

//********TYPES STARTS*********/
// Amounts come back as strings (MySQL DECIMAL). Booleans as 0/1. Rows keep their
// numeric `id`. Interfaces carry an index signature so the full object fetched
// from API 1 can be sent back to API 2 unchanged (read-only fields are ignored
// server-side).
export interface HotelTransaction {
  id?: number;
  itinerary_status?: string;
  internal_status?: string;
  payment_status?: string;
  travel_start_date?: string;
  travel_end_date?: string;
  total_usr_amt?: string;
  sup_total_amt?: string;
  us_cur_total_amt?: string;
  [k: string]: unknown;
}

export interface HotelDetail {
  id: number;
  hotel_name?: string;
  country?: string;
  city?: string;
  check_in?: string;
  check_out?: string;
  status?: string;
  internal_status?: string;
  no_of_rooms?: number | string;
  adult_count?: number | string;
  child_count?: number | string;
  child_ages?: string | null;
  room_name?: string;
  room_id?: string;
  is_refundable?: number | boolean;
  board_type?: string;
  sup_base_fare?: string;
  sup_taxes?: string;
  surcharges?: string;
  sup_total_fare?: string;
  cancellation_policy?: string;
  [k: string]: unknown;
}

export interface HotelPassenger {
  id: number;
  room_no?: number | string;
  pax_serial_no?: string;
  pax_type?: string;
  title?: string;
  first_name?: string;
  middle_name?: string | null;
  last_name?: string;
  email?: string;
  country_code?: string;
  phone_no?: string;
  is_lead_pax?: number | boolean;
  country_name?: string;
  iso?: string;
  [k: string]: unknown;
}

export interface HotelPaymentBreakdown {
  id: number;
  hotel_price?: string;
  total_price?: string;
  discount_amount?: string;
  actual_price?: string;
  final_price?: string;
  promo_applied?: number | boolean;
  promo_code?: string | null;
  hotel_discount?: string;
  hotel_checkin_due?: string;
  [k: string]: unknown;
}

export interface HotelPaymentDetail {
  id: number;
  payment_type?: string;
  pay_amount?: string;
  credit_balance?: string | null;
  card_type?: string;
  customer_name?: string;
  pay_curr_code?: string;
  payment_status?: string;
  payment_remarks?: string | null;
  [k: string]: unknown;
}

export interface HotelBookingData {
  itinerary_id: string;
  transaction?: HotelTransaction;
  hotel_details: HotelDetail[];
  hotel_passenger_details: HotelPassenger[];
  payment_breakdown: HotelPaymentBreakdown | null;
  payment_details: HotelPaymentDetail[];
  [k: string]: unknown;
}

export interface ApiEnvelope<T> {
  status: number;
  message?: string;
  error?: string;
  details?: { validationErrors?: string[] };
  data: T;
}

// Payload sent to API 2. Every array row must keep its numeric `id`.
export interface UpdateHotelPayload {
  itinerary_id: string;
  modified_by: string;
  transaction?: HotelTransaction;
  hotel_details: HotelDetail[];
  hotel_passenger_details?: HotelPassenger[];
  payment_breakdown?: HotelPaymentBreakdown | null;
  payment_details?: HotelPaymentDetail[];
}
//********TYPES ENDS*********/

//********HELPERS STARTS*********/
// The logged-in ops user's email — pre-populates the "Modified By" field and is
// sent as modified_by on update. Mirrors getAdminId() in manualBookingService.
export const getModifiedBy = (): string => {
  const u = getUserDetails();
  return u?.email || "";
};

// Normalize to the engine envelope { status, message, data }. The gateway may
// preserve that shape as-is, or wrap it as { statusCode, message, data:{...} }.
// Detect the wrapper by the inner `.data.status` and peel it off.
const unwrap = <T>(res: any): ApiEnvelope<T> =>
  res && res.data && res.data.status !== undefined ? res.data : res;
//********HELPERS ENDS*********/

//********SERVICE FUNCTIONS STARTS*********/
/**
 * API 1 — fetch the consolidated hotel booking by itinerary id.
 * Dashes in the id are ignored server-side. PNR lookup is not supported yet.
 */
export const fetchHotelBooking = (
  itineraryId: string,
  opts: object = {}
): Promise<ApiEnvelope<HotelBookingData>> =>
  apiPost(
    `${HOTEL_EDIT_BASE}/fetch`,
    { itinerary_id: itineraryId },
    { ...opts, traceId: genTraceId() }
  ).then((res: any) => unwrap<HotelBookingData>(res));

/**
 * API 2 — persist the edited booking (all 5 tables in one DB transaction).
 * On 200, callers should re-fetch to confirm the persisted values.
 */
export const updateHotelBooking = (
  payload: UpdateHotelPayload,
  opts: object = {}
): Promise<ApiEnvelope<{ itinerary_id: string; modified_by: string }>> =>
  apiPost(`${HOTEL_EDIT_BASE}/update`, payload, { ...opts, traceId: genTraceId() }).then(
    (res: any) => unwrap<{ itinerary_id: string; modified_by: string }>(res)
  );
//********SERVICE FUNCTIONS ENDS*********/

export default { fetchHotelBooking, updateHotelBooking, getModifiedBy };
